import { CartOutput } from "../../../services/redux/types/CartLoyaltyV2Types";
import { OrderTypesEnum, OrderTypesKeysEnum } from "../../enums";
import {
  CategoryType,
  PreferredCountryEnum,
  Restaurant,
  SubItemType,
  allPricesType,
} from "../../types";
import {
  addWithRoundingOrCurrency,
  multiplyWithRoundingOrCurrency,
  subtractWithRoundingOrCurrency,
} from "../maths/maths";
import * as pricingHelper from "./pricing";

export const getAllPrices = (
  restaurantInfo: Restaurant,
  cartV2?: CartOutput
) => {
  const allPrices: allPricesType = {
    subTotal: 0,
    paidPrice: 0,
    subTotalWithDiscount: undefined,
    vat: undefined,
    service: undefined,
    deliveryFees: undefined,
    discount: undefined,
  };
  const hasRoundingMethodAndLevel =
    !!restaurantInfo?.currencyRoundingLevel &&
    !!restaurantInfo?.currencyRoundingMethod;

  if (cartV2) {
    allPrices.subTotal = cartV2.subTotalPrice;
    allPrices.paidPrice = cartV2.totalPrice;
    allPrices.vat = cartV2.vat;
    allPrices.service = cartV2.service;
    allPrices.deliveryFees = cartV2.deliveryFees;
    allPrices.discount = cartV2.discountedValue;
    allPrices.subTotalWithDiscount = subtractWithRoundingOrCurrency(
      hasRoundingMethodAndLevel,
      cartV2.subTotalPrice,
      cartV2.discountedValue
    );

    return allPrices;
  }
};

export const prepareValueWithCurrency = (value: string, currency: string) => {
  value = value.toString().trim();
  currency = currency.toString().trim();

  return `${currency === "$" ? currency : ``}${value}${
    currency !== "$" ? ` ${currency}` : ``
  }`;
};

export const addServiceCheck = (
  preferredCountry: PreferredCountryEnum,
  orderType?: OrderTypesEnum | OrderTypesKeysEnum
) => {
  return (
    orderType === OrderTypesEnum.DINE_IN ||
    (orderType === OrderTypesKeysEnum.DINE_IN &&
      preferredCountry === PreferredCountryEnum.EG)
  );
};

export const getVatServiceTotal = (
  restaurantInfo: Restaurant,
  orderType: OrderTypesEnum
) => {
  const service =
    restaurantInfo.preferredCountry === PreferredCountryEnum.EG &&
    restaurantInfo.service > 0 &&
    orderType === OrderTypesEnum.DINE_IN
      ? restaurantInfo.service
      : 0;
  const totalFees = restaurantInfo.vat + service;

  return { totalFees, vat: restaurantInfo.vat, service };
};

export const getFranchiseCustomPricesHelper = (
  franchiseId: number,
  oldPrice: number,
  customPriceArray?: any[]
) => {
  if (customPriceArray && customPriceArray.length > 0) {
    const customePrice = customPriceArray.find((customPrice) => {
      return customPrice.franchiseId === franchiseId;
    });

    if (customePrice) {
      return customePrice.price;
    }
  }

  return oldPrice;
};

export const getHighestCustomPriceHelper = (
  basePrice: number,
  customFranchiseSubItemPrices: {
    franchiseId: number;
    price: number;
  }[]
) => {
  const highestCustomPrice = customFranchiseSubItemPrices.reduce(
    (max, price) => {
      return Math.max(max, price.price);
    },
    0
  );

  return Math.max(basePrice, highestCustomPrice);
};

export const getFranchiseCustomPrices = (
  subitems: SubItemType[],
  franchiseId: number
) => {
  subitems.forEach((subitem) => {
    if (franchiseId > 0) {
      subitem.price = pricingHelper.getFranchiseCustomPricesHelper(
        franchiseId,
        subitem.basePrice,
        subitem.customFranchiseSubItemPrices
      );
    } else {
      subitem.price = getHighestCustomPriceHelper(
        subitem.basePrice,
        subitem.customFranchiseSubItemPrices
      );
    }

    subitem.allAddonGroups?.forEach((AddonCategory) => {
      AddonCategory.addonGroupSubItem?.forEach((addonGroupSubItem) => {
        addonGroupSubItem.price = pricingHelper.getFranchiseCustomPricesHelper(
          franchiseId,
          addonGroupSubItem.basePrice,
          addonGroupSubItem.customFranchiseAddonGroupSubItemPrices
        );

        if (addonGroupSubItem.subItem.allAddonGroups) {
          addonGroupSubItem.subItem.allAddonGroups.forEach(
            (nestedAddonGroup) => {
              nestedAddonGroup.addonGroupSubItem.forEach(
                (nestedAddongroupSubItem) => {
                  nestedAddongroupSubItem.price =
                    pricingHelper.getFranchiseCustomPricesHelper(
                      franchiseId,
                      nestedAddongroupSubItem.basePrice,
                      nestedAddongroupSubItem.customFranchiseAddonGroupSubItemPrices
                    );
                }
              );
            }
          );
        }
      });
    });
  });
};

export const getPriceWithVatOrService = (
  price: number,
  VATOrService: number,
  hasRoundingMethodAndLevel: boolean
) => {
  const addOneToVatOrService = addWithRoundingOrCurrency(
    hasRoundingMethodAndLevel,
    1,
    VATOrService
  );

  return multiplyWithRoundingOrCurrency(
    hasRoundingMethodAndLevel,
    addOneToVatOrService,
    price
  );
};

export const setAllCategoriesItemsCustomPricesPerFranchise = (
  allCategories: CategoryType[],
  franchiseId: number
) => {
  allCategories.forEach((category) => {
    category.itemCategories.forEach((itemCategory) => {
      pricingHelper.getFranchiseCustomPrices(
        itemCategory.item.subItems,
        franchiseId
      );
    });
  });

  return allCategories;
};

export const countryToCurrency = (country: PreferredCountryEnum) => {
  switch (country) {
    case PreferredCountryEnum.EG:
      return "EGP";
    case PreferredCountryEnum.SA:
      return "SAR";
    case PreferredCountryEnum.CA:
      return "CAD";
  }
};
